import ExpandMoreIcon from "@mui/icons-material/ExpandMoreOutlined"
import SMSOutlinedIcon from "@mui/icons-material/SmsOutlined"
import LoadingButton from "@mui/lab/LoadingButton"
import AccordionDetails from "@mui/material/AccordionDetails"
import AccordionSummary from "@mui/material/AccordionSummary"
import Button from "@mui/material/Button"
import Divider from "@mui/material/Divider"
import InputAdornment from "@mui/material/InputAdornment"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { compareAsc } from "date-fns"
import { useState } from "react"

import { useAuthorizedAxiosClient } from "../../../../../common/axios"
import CloseableSnackbar, {
  useCloseableSnackbarState,
} from "../../../../../common/components/CloseableSnackbar"
import { CenterEverythingColumn } from "../../../../../common/components/Column"
import Column from "../../../../../common/components/Column"
import {
  firstApiErrorMessage,
  formatDateTime,
} from "../../../../../common/utils"
import { TRANSACTION_DIRECTIONS } from "../../../utils"
import { EVENT_TYPES, GLOBAL_SEARCH_URL_PARAMS } from "../../data"
import {
  AccordionDetailsContent,
  AccordionDetailsTypography,
  AccordionSummaryContent,
  StyledAccordion,
} from "./Accordions"
import {
  AccordionCounterTitle,
  HeaderNameValue,
  shouldShowTransactionDivider,
  useURLBoundExpandedAccordion,
} from "./EmailsTab"
import { outgoingSmsPerformerName } from "./PerformedByAttribution"
import TabColumn from "./TabColumn"
import TabContentColumn from "./TabContentColumn"
import TabHeaderRow from "./TabHeaderRow"
import ActivitySkeleton from "./components/ActivitySkeleton"
import { useNotificationUtils } from "./utils"

export default function SMSTab({
  candidate,
  setCandidate,
  onSendSMSButtonClicked,
  isLoading = false,
  readOnly = false,
}) {
  const [isReplying, setIsReplying] = useState(false)
  const [replyText, setReplyText] = useState("")
  const [isSendingReply, setIsSendingReply] = useState(false)
  const {
    close: closeSnackbar,
    isOpen: isSnackbarOpen,
    message: snackbarMessage,
    open: openSnackbar,
    severity: snackbarSeverity,
  } = useCloseableSnackbarState()
  const smsEvents = candidate.crm_events.filter(
    (event) => event.type === EVENT_TYPES.sms
  )
  const { expandedEventID, setExpandedEventID } = useURLBoundExpandedAccordion(
    candidate,
    setCandidate,
    smsEvents,
    GLOBAL_SEARCH_URL_PARAMS.sms
  )
  const axios = useAuthorizedAxiosClient()
  const { getHasUnreadTransactions, markEventTransactionsRead } =
    useNotificationUtils()

  function handleAccordionExpandChanged(event, isExpanded) {
    if (isExpanded) {
      setExpandedEventID(event.id)
      markEventTransactionsRead(candidate, setCandidate, event)
    } else {
      setExpandedEventID(null)
    }
  }

  function handleReplyButtonClicked() {
    if (isReplying) {
      setIsSendingReply(true)
      axios
        .post(`/workflows/api/sms/reply-thread/${expandedEventID}/`, {
          message: replyText,
        })
        .then(({ data }) => {
          setReplyText("")
          const smsSendEvent = smsEvents.filter(
            (event) => event.id === expandedEventID
          )[0]
          const newSMSSendEvent = {
            ...smsSendEvent,
            transactions: [...smsSendEvent.transactions, data],
          }
          setCandidate({
            ...candidate,
            crm_events: candidate.crm_events.map((event) =>
              event.id === expandedEventID ? newSMSSendEvent : event
            ),
          })
          openSnackbar("Reply has been sent successfully")
          setIsReplying(false)
        })
        .catch((err) => {
          openSnackbar(
            firstApiErrorMessage(
              err?.response?.data,
              "Could not send SMS. Please try again."
            ),
            "error"
          )
        })
        .finally(() => {
          setIsSendingReply(false)
        })
    } else {
      setIsReplying(true)
    }
  }

  return (
    <TabColumn>
      <TabHeaderRow title="SMS History">
        <Button
          variant="outlined"
          color="inherit"
          startIcon={<SMSOutlinedIcon />}
          onClick={onSendSMSButtonClicked}
          disabled={Boolean(candidate.is_dnc)}
        >
          Send SMS
        </Button>
      </TabHeaderRow>
      <TabContentColumn>
        {isLoading ? (
          <ActivitySkeleton />
        ) : smsEvents.length === 0 ? (
          <CenterEverythingColumn>
            <Typography variant="body1" color="text.secondary">
              No SMS sent yet.
            </Typography>
          </CenterEverythingColumn>
        ) : (
          smsEvents.map((event) => (
            <StyledAccordion
              expanded={event.id === expandedEventID}
              onChange={(e, isExpanded) =>
                handleAccordionExpandChanged(event, isExpanded)
              }
              key={event.id}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <AccordionSummaryContent
                  Icon={<SMSOutlinedIcon />}
                  accordionTitle={
                    <AccordionCounterTitle
                      title={event.campaign?.name || "SMS"}
                      count={(event.transactions || []).length}
                      hasNew={getHasUnreadTransactions(event)}
                    />
                  }
                  dateStr={event.created}
                />
              </AccordionSummary>
              <AccordionDetails>
                <AccordionDetailsContent>
                  <Column sx={{ width: "100%" }}>
                    {(event.transactions || [])
                      .sort((t1, t2) =>
                        compareAsc(new Date(t1.created), new Date(t2.created))
                      )
                      .map((transaction, i) => (
                        <Column key={transaction.id}>
                          <Column sx={{ gap: 2 }}>
                            <Column sx={{ gap: 0.5 }}>
                              <HeaderNameValue
                                name="From"
                                value={
                                  transaction.direction ===
                                  TRANSACTION_DIRECTIONS.incoming
                                    ? candidate.candidate_name
                                    : outgoingSmsPerformerName(transaction) ||
                                      "—"
                                }
                              />
                              <HeaderNameValue
                                name="Date"
                                value={formatDateTime(transaction.created)}
                              />
                            </Column>
                            <AccordionDetailsTypography>
                              {transaction.text}
                            </AccordionDetailsTypography>
                          </Column>
                          {shouldShowTransactionDivider(
                            event.transactions,
                            i
                          ) ? (
                            <Divider sx={{ my: 2 }} />
                          ) : null}
                        </Column>
                      ))}
                    {readOnly ? null : (
                      <TextField
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        disabled={!isReplying}
                        inputRef={(ref) => ref && isReplying && ref.focus()}
                        placeholder="Type a message here..."
                        sx={{
                          mt: 2,
                          backgroundColor: "white",
                          "& .MuiInputBase-input": {
                            paddingBlock: 2,
                          },
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <LoadingButton
                                variant="contained"
                                color="primary"
                                size="small"
                                loading={isSendingReply}
                                disabled={isReplying && replyText.length === 0}
                                onClick={handleReplyButtonClicked}
                              >
                                {isReplying ? "Send" : "Reply"}
                              </LoadingButton>
                            </InputAdornment>
                          ),
                        }}
                        fullWidth
                      />
                    )}
                  </Column>
                </AccordionDetailsContent>
              </AccordionDetails>
            </StyledAccordion>
          ))
        )}
      </TabContentColumn>
      <CloseableSnackbar
        message={snackbarMessage}
        onClose={closeSnackbar}
        open={isSnackbarOpen}
        severity={snackbarSeverity}
      />
    </TabColumn>
  )
}
